/**
 * segmenter.ts — turns the lossless hast tree into a GridPlan: a flat list of
 * master grid rows (the gutter side) plus placed content blocks (the content
 * side). The renderer (step 3) maps a GridPlan onto CSS grid/subgrid with no
 * measurement.
 *
 * Core invariant: every distinct annotatable unit gets its own master row;
 * multi-row content spans (`grid-row: span N`). Within one table row:
 *   - the gutter-OWNER column (most annotated sub-items; tie → lowest index)
 *     gets one body row per item;
 *   - the row-level annotation and every OTHER column's annotations become
 *     leading rows (a non-owner column with multiple items collapses to a
 *     single aggregate marker — the graceful degrade for unpredictable API
 *     data).
 */

import { ElementNode, HastNode, nodeText } from "./hast";
import { deriveColumnFractions } from "./tableColumns";
import { blockId as makeBlockId, columnLabels, tableId as makeTableId } from "./tableVisibility";
import { computeTableLayout, Placement } from "./tableLayout";

// Void elements that carry content on their own — a block holding only one of
// these is NOT empty (e.g. a bare image or rule). Everything else void (br, wbr…)
// is treated as visual whitespace.
const MEANINGFUL_VOID = new Set(["img", "hr"]);

function hasMeaningfulVoid(node: HastNode): boolean {
    if (node.type !== "element") return false;
    if (MEANINGFUL_VOID.has(node.tagName)) return true;
    return node.children.some(hasMeaningfulVoid);
}

function hasBlockKey(node: HastNode): boolean {
    if (node.type !== "element") return false;
    if (node.properties["data-block-key"]) return true;
    return node.children.some(hasBlockKey);
}

/** Whether an element carries anything worth a grid row of its own: visible
 *  text, an annotation anchor, or real void content (an image / rule). */
function earnsRow(node: HastNode): boolean {
    return nodeText(node).trim() !== "" || hasBlockKey(node) || hasMeaningfulVoid(node);
}

/**
 * Whether a TOP-LEVEL node earns a grid row. PDF-extracted HTML is littered with
 * inter-element whitespace text nodes, blank paragraphs (`<p></p>`, `<p>&nbsp;</p>`,
 * `<p><br></p>`) and comments; placing each as its own row injects empty bands
 * (the classic view collapsed them inside one per-element box). Drop a node only
 * when it is visually empty AND carries nothing addressable — never inside a
 * table/cell, where an empty `<td>` is structural; this guard is top-level only.
 */
function isRenderableTop(node: HastNode): boolean {
    if (node.type === "comment") return false;
    if (node.type === "text") return node.value.trim() !== "";
    return earnsRow(node);
}

export type NoteCode = "F" | "U" | "D" | "X" | "V" | "A" | "";
export type BlockKey = string;

export interface AnnotationData {
    note?: NoteCode;
    comment?: string;
    deptId?: number;
    docLinkId?: number;
}

export type AnnotationLookup = Record<BlockKey, AnnotationData>;

export type GutterCell =
    | { kind: "none" }
    | { kind: "bubble"; blockKey: BlockKey; note?: NoteCode; comment?: string }
    | { kind: "aggregate"; blockKeys: BlockKey[]; count: number };

export interface GridRow {
    row: number; // 1-based master grid line
    gutter: GutterCell;
    dept?: { blockKey: BlockKey; deptId: number };
    docLink?: { blockKey: BlockKey; docLinkId: number };
}

export interface PlacedBlock {
    node: HastNode;
    rowStart: number; // 1-based, relative to the enclosing grid
    rowSpan: number;
    colStart: number;
    colSpan: number;
    /** present for containers rendered as their own subgrid (table, top-level list) */
    subgrid?: {
        rows: number;
        cols: number;
        children: PlacedBlock[];
        /**
         * Relative column widths (used as `fr` weights) derived from cell
         * `data-bbox` attributes. Absent when the table has no usable bbox data,
         * in which case the renderer falls back to CSS intrinsic sizing.
         */
        colFractions?: number[];
        /**
         * Advanced mode only: the table's column delete/restore controls — a
         * horizontal band above the table, one control per column, occupying
         * subgrid row 1. Present only when `SegmentOptions.tableToolbar` is set.
         *
         * The band is a real grid row reserved by the segmenter, so each control
         * lands on its own column's track and lines up with it without any
         * measurement.
         */
        toolbar?: {
            tableId: string;
            /** One per logical column, in column order. */
            columnLabels: string[];
        };
    };
    /**
     * Annotated key of the enclosing <tr>, propagated onto each cell. A table
     * row has no rendered DOM element of its own (cells are direct grid items),
     * so cells carry the row's key to make a row-level annotation selectable
     * and highlightable.
     */
    rowKey?: BlockKey;
    /**
     * Height, in grid rows, of the band the enclosing <tr> occupies — set
     * together with `rowKey`. NOT derivable from the cells: `rowSpan` on a
     * rowspanning cell covers its own row PLUS the rows below it, so a renderer
     * that reads the first cell's span to size a row-level decoration would
     * cover the whole span (the row-highlight overlay drawing over a table's
     * entire body). The segmenter knows the row's own band while placing it, so
     * it records it rather than leaving the renderer to infer it.
     */
    rowBand?: number;
    /**
     * Source backend element id (ImportedDocumentElement.id), set on TOP-LEVEL
     * blocks only. One element's contentText may expand into several top blocks,
     * so this is a many-to-one back-reference — handy for verifying the data
     * mapping in the rendered DOM (`data-element-id`).
     */
    elementId?: string;
    /**
     * Stable identity of a TOP-LEVEL block across reloads (element id + the
     * node's ordinal within that element). Unlike the index into `plan.content`
     * — which shifts whenever an earlier node stops being renderable — this
     * survives edits, so it is what "hide this paragraph" is persisted against.
     */
    blockId?: string;
    /**
     * Nesting level of a list item (0 = outermost). Nested items are lifted out
     * of their parent <li> into rows of their own, which flattens the markup —
     * the renderer restores the visual hierarchy by indenting on this.
     */
    depth?: number;
}

export interface GridPlan {
    rowCount: number;
    rows: GridRow[];
    content: PlacedBlock[];
}

export interface SegmentOptions {
    /** Reserve a leading grid row per table for the column delete/restore
     *  controls (advanced mode). Off by default — the read layout is unchanged. */
    tableToolbar?: boolean;
}

// ---------------------------------------------------------------------------

function isElement(n: HastNode): n is ElementNode {
    return n.type === "element";
}

/**
 * Every `data-block-key` in the tree, in document order (deduped). This is the
 * full set of annotatable units — the static equivalent of the classic view's
 * DOM scan for `[data-block-key]`, used to drive filtering and Prev/Next.
 */
export function collectBlockKeys(nodes: HastNode[]): BlockKey[] {
    const out: BlockKey[] = [];
    const seen = new Set<BlockKey>();
    const visit = (list: HastNode[]) => {
        for (const n of list) {
            if (!isElement(n)) continue;
            const key = n.properties["data-block-key"];
            if (key && !seen.has(key)) {
                seen.add(key);
                out.push(key);
            }
            visit(n.children);
        }
    };
    visit(nodes);
    return out;
}

function annotatedKey(node: HastNode, ann: AnnotationLookup): BlockKey | undefined {
    if (!isElement(node)) return undefined;
    const key = node.properties["data-block-key"];
    return key && ann[key] ? key : undefined;
}

function markerCell(key: BlockKey, ann: AnnotationLookup): GutterCell {
    const a = ann[key];
    if (a && (a.comment || a.note)) {
        return { kind: "bubble", blockKey: key, note: a.note, comment: a.comment };
    }
    return { kind: "none" };
}

/** One renderable unit of an owner cell — an annotated element (carries `key`)
 *  or a chunk of non-annotated content that must still be shown. */
interface CellUnit {
    node: HastNode;
    key?: BlockKey;
    /** List nesting level, for indentation (see PlacedBlock.depth). */
    depth?: number;
}

const LIST_TAGS = new Set(["ul", "ol"]);

function nestedLists(li: ElementNode): ElementNode[] {
    return li.children.filter((c): c is ElementNode => isElement(c) && LIST_TAGS.has(c.tagName));
}

/** An <li>'s content minus the nested lists that are being given rows of their
 *  own. Lists we are NOT exploding (decorative ones) stay inline in the item. */
function ownContent(li: ElementNode, exploded: ElementNode[]): ElementNode {
    return { ...li, children: li.children.filter((c) => !exploded.includes(c as ElementNode)) };
}

/**
 * Explodes a list into one unit per item, at ANY depth.
 *
 * A nested <li> is an annotatable unit in its own right, so it needs a master
 * row of its own: leaving it inside its parent <li>'s markup gives it nowhere to
 * hang a gutter cell, and its comment silently vanishes — while its key still
 * counts towards the filter/Prev-Next totals. That was the nested-list bug.
 *
 * The explode is driven by the presence of `data-block-key`, NOT by whether the
 * item happens to be annotated yet: the layout must not restructure itself the
 * moment a user adds the first comment. A purely decorative nested list (no keys
 * anywhere in it) is left alone inside its parent item.
 */
function listUnits(list: ElementNode, ann: AnnotationLookup, depth = 0, out: CellUnit[] = []): CellUnit[] {
    for (const li of list.children) {
        if (!isElement(li) || li.tagName !== "li") continue;
        const nested = nestedLists(li).filter(hasBlockKey);
        if (nested.length === 0) {
            out.push({ node: li, key: annotatedKey(li, ann), depth });
            continue;
        }
        // Skip the row of a wrapper item that holds nothing but the nested list —
        // it would render as an empty band.
        const own = ownContent(li, nested);
        if (earnsRow(own)) out.push({ node: own, key: annotatedKey(li, ann), depth });
        for (const sub of nested) listUnits(sub, ann, depth + 1, out);
    }
    return out;
}

function hasAnnotatedDescendant(node: HastNode, ann: AnnotationLookup): boolean {
    if (!isElement(node)) return false;
    if (annotatedKey(node, ann)) return true;
    return node.children.some((c) => hasAnnotatedDescendant(c, ann));
}

/**
 * Annotation profile of a table cell:
 *  - `cellKey`: the cell's own data-block-key, if annotated
 *  - `extraKeys`: annotated keys that need a gutter marker but own no content row
 *    (a <ul> annotated as a whole, whose items each take a row of their own)
 *  - `items`: annotated units (e.g. <li>s, at any depth) — drive owner selection
 *    and the non-owner degrade
 *  - `units`: the cell's content in document order, with annotatable elements
 *    pulled out as their own units and non-annotated content (intro lines,
 *    uncommented siblings) kept as filler units. Used to render the OWNER cell
 *    so nothing is dropped — every unit gets a body row, but only annotated
 *    ones get a gutter bubble.
 */
function cellProfile(
    cell: ElementNode,
    ann: AnnotationLookup,
): { cellKey?: BlockKey; extraKeys: BlockKey[]; items: ElementNode[]; units: CellUnit[] } {
    const cellKey = annotatedKey(cell, ann);
    const extraKeys: BlockKey[] = [];
    const items: ElementNode[] = [];
    const units: CellUnit[] = [];
    const visit = (children: HastNode[]) => {
        for (const c of children) {
            if (isElement(c)) {
                if (LIST_TAGS.has(c.tagName) && hasBlockKey(c)) {
                    // A list is exploded item-by-item, nested items included, so
                    // each one can own a row. The list's own annotation (if any)
                    // has no content row left to sit on → it becomes a marker.
                    const listKey = annotatedKey(c, ann);
                    if (listKey) extraKeys.push(listKey);
                    for (const u of listUnits(c, ann)) {
                        if (u.key) items.push(u.node as ElementNode);
                        units.push(u);
                    }
                    continue;
                }
                const key = annotatedKey(c, ann);
                if (key) {
                    // topmost annotated element → its own unit, don't descend
                    items.push(c);
                    units.push({ node: c, key });
                } else if (c.children.some((g) => hasAnnotatedDescendant(g, ann))) {
                    // wrapper around annotated content → pull the annotated
                    // descendants out, interspersing non-annotated siblings
                    visit(c.children);
                } else {
                    // non-annotated element with nothing annotated inside → keep whole
                    units.push({ node: c });
                }
            } else if (c.type === "text" && c.value.trim() !== "") {
                units.push({ node: c }); // significant bare text; skip whitespace + comments
            }
        }
    };
    visit(cell.children);
    return { cellKey, extraKeys, items, units };
}

/** How many tables the subtree holds, node inclusive — the unit table ids count in. */
function countTables(node: HastNode): number {
    if (!isElement(node)) return 0;
    return (node.tagName === "table" ? 1 : 0) + node.children.reduce((n, c) => n + countTables(c), 0);
}

function tableRows(table: ElementNode): ElementNode[] {
    const out: ElementNode[] = [];
    for (const child of table.children) {
        if (!isElement(child)) continue;
        if (child.tagName === "tr") out.push(child);
        else if (["thead", "tbody", "tfoot"].includes(child.tagName)) {
            for (const tr of child.children) {
                if (isElement(tr) && tr.tagName === "tr") out.push(tr);
            }
        }
    }
    return out;
}

// ---------------------------------------------------------------------------

export function segment(
    nodes: HastNode[],
    ann: AnnotationLookup,
    /** Optional per-node source element id, aligned 1:1 with `nodes` (pre-filter). */
    elementIds?: Array<string | undefined>,
    opts?: SegmentOptions,
): GridPlan {
    let cursor = 0; // last allocated master row (next = cursor + 1)
    const gutter = new Map<number, GutterCell>();
    const dept = new Map<number, { blockKey: BlockKey; deptId: number }>();
    const docLink = new Map<number, { blockKey: BlockKey; docLinkId: number }>();

    const take = (n: number): number => {
        const start = cursor + 1;
        cursor += n;
        return start;
    };

    const assign = (row: number, cell: GutterCell, key?: BlockKey) => {
        gutter.set(row, cell);
        if (key) {
            const a = ann[key];
            if (a?.deptId != null) dept.set(row, { blockKey: key, deptId: a.deptId });
            if (a?.docLinkId != null) docLink.set(row, { blockKey: key, docLinkId: a.docLinkId });
        }
    };

    const placeLeaf = (node: HastNode): PlacedBlock => {
        const row = take(1);
        const key = annotatedKey(node, ann);
        if (key) assign(row, markerCell(key, ann), key);
        return { node, rowStart: row, rowSpan: 1, colStart: 1, colSpan: 1 };
    };

    const placeList = (list: ElementNode): PlacedBlock => {
        // One unit per item at every depth — a nested item is not swallowed by
        // its parent, it gets a row (and therefore a gutter cell) of its own.
        const units = listUnits(list, ann);
        if (units.length === 0) return placeLeaf(list);

        const extras: Array<{ cell: GutterCell; key?: BlockKey }> = [];
        const listKey = annotatedKey(list, ann);
        if (listKey) extras.push({ cell: markerCell(listKey, ann), key: listKey });

        const band = extras.length + units.length;
        const base = take(band);
        extras.forEach((e, i) => assign(base + i, e.cell, e.key));

        const bodyBase = base + extras.length;
        const children: PlacedBlock[] = units.map((u, i) => {
            const row = bodyBase + i;
            if (u.key) assign(row, markerCell(u.key, ann), u.key);
            return {
                node: u.node,
                rowStart: row - base + 1,
                rowSpan: 1,
                colStart: 1,
                colSpan: 1,
                depth: u.depth,
            };
        });

        return {
            node: list,
            rowStart: base,
            rowSpan: band,
            colStart: 1,
            colSpan: 1,
            subgrid: { rows: band, cols: 1, children },
        };
    };

    /** Cell whose final grid rowSpan must wait until all rows' bands are known. */
    interface PendingRowSpan {
        block: PlacedBlock;
        rowIdx: number;
        rowSpan: number;
    }

    const placeTableRow = (
        tr: ElementNode,
        rowPlacements: Placement[],
        rowIdx: number,
        tableBase: number,
        out: PlacedBlock[],
        pending: PendingRowSpan[],
    ) => {
        const cells = rowPlacements.map((p) => p.cell);
        const profiles = cells.map((c) => cellProfile(c, ann));
        const demands = profiles.map((p) => p.items.length);

        let ownerIdx = -1;
        let best = 0;
        demands.forEach((d, i) => {
            if (d > best) {
                best = d;
                ownerIdx = i;
            }
        });
        // The owner gets one body row per content unit (annotated AND filler),
        // so non-annotated content in the owner cell is rendered, not dropped.
        const ownerUnits = ownerIdx >= 0 ? profiles[ownerIdx].units : [];
        const ownerDemand = ownerUnits.length;

        // Leading markers: tr-level + every non-owner cell's annotations.
        const extras: Array<{ cell: GutterCell; key?: BlockKey }> = [];
        const trKey = annotatedKey(tr, ann);
        if (trKey) extras.push({ cell: markerCell(trKey, ann), key: trKey });

        // A <tr> has no rendered DOM element, so its key is propagated onto every
        // cell (see PlacedBlock.rowKey). Use the attribute's PRESENCE, not whether
        // it's annotated yet — otherwise an unevaluated row can't be clicked to
        // select it (chicken-and-egg: you select a row in order to evaluate it).
        const trSelectKey = tr.properties["data-block-key"];

        cells.forEach((_, i) => {
            const p = profiles[i];
            const marker = (k: BlockKey) => extras.push({ cell: markerCell(k, ann), key: k });
            if (i === ownerIdx) {
                if (p.cellKey) marker(p.cellKey);
                p.extraKeys.forEach(marker);
                return;
            }
            if (p.cellKey) marker(p.cellKey);
            p.extraKeys.forEach(marker);
            if (p.items.length === 1) {
                const k = annotatedKey(p.items[0], ann)!;
                extras.push({ cell: markerCell(k, ann), key: k });
            } else if (p.items.length > 1) {
                const keys = p.items.map((it) => annotatedKey(it, ann)!);
                extras.push({ cell: { kind: "aggregate", blockKeys: keys, count: keys.length } });
            }
        });

        // Dev guard: annotations on a rowspanning cell are the one case the
        // static model can't arbitrate (see design note). Surface it loudly.
        if (process.env.NODE_ENV !== "production") {
            rowPlacements.forEach((p, i) => {
                if (p.rowSpan > 1 && (profiles[i].cellKey || profiles[i].items.length > 0)) {
                    // eslint-disable-next-line no-console
                    console.warn(
                        `[segmenter] annotation on a rowspan=${p.rowSpan} cell — static alignment may collide`,
                        { cell: p.cell.properties },
                    );
                }
            });
        }

        const band = ownerDemand === 0 ? Math.max(1, extras.length) : extras.length + ownerDemand;
        const base = take(band);

        extras.forEach((e, i) => assign(base + i, e.cell, e.key));
        if (ownerDemand > 0) {
            const bodyBase = base + extras.length;
            ownerUnits.forEach((u, i) => {
                // Annotated units get a bubble; filler units leave the gutter empty.
                if (u.key) assign(bodyBase + i, markerCell(u.key, ann), u.key);
            });
        }

        // Content side: each cell uses its resolved column from the layout pass
        // (so cells under a rowspan from above land in the right column instead
        // of shifting left). Cells with rowSpan > 1 get queued — their final
        // grid rowSpan can only be set once we know every row's band height.
        rowPlacements.forEach((p, i) => {
            const block: PlacedBlock = {
                node: p.cell,
                rowStart: base - tableBase + 1,
                rowSpan: band, // patched in post-pass for rowspan > 1
                colStart: p.col + 1, // grid is 1-based; placement col is 0-based
                colSpan: p.colSpan,
            };
            if (trSelectKey) {
                block.rowKey = trSelectKey;
                block.rowBand = band;
            }
            if (i === ownerIdx && ownerDemand > 0) {
                const children: PlacedBlock[] = ownerUnits.map((u, j) => ({
                    node: u.node,
                    rowStart: extras.length + j + 1, // body rows, 1-based within the cell band
                    rowSpan: 1,
                    colStart: 1,
                    colSpan: 1,
                    depth: u.depth,
                }));
                block.subgrid = { rows: band, cols: 1, children };
            }
            out.push(block);
            if (p.rowSpan > 1) pending.push({ block, rowIdx, rowSpan: p.rowSpan });
        });
    };

    const placeTable = (table: ElementNode, tableId?: string): PlacedBlock => {
        const trs = tableRows(table);
        const layout = computeTableLayout(trs);
        // Group placements by row for per-row processing while keeping source order.
        const byRow: Placement[][] = trs.map(() => []);
        for (const p of layout.placements) byRow[p.rowIdx].push(p);

        const tableBase = cursor + 1;
        // Advanced mode: reserve one leading band for the column toolbar. Taken
        // BEFORE the rows, so every row's rowStart — which is relative to
        // tableBase — shifts down by one on its own and the controls get grid
        // row 1 of the table's subgrid.
        if (opts?.tableToolbar) take(1);

        const cells: PlacedBlock[] = [];
        const bands: number[] = [];
        const pending: PendingRowSpan[] = [];

        for (let rowIdx = 0; rowIdx < trs.length; rowIdx++) {
            const before = cursor;
            placeTableRow(trs[rowIdx], byRow[rowIdx], rowIdx, tableBase, cells, pending);
            bands[rowIdx] = cursor - before;
        }

        // Finalize rowspan grid spans: a cell with rowSpan = N covers the bands
        // of rows [rowIdx .. rowIdx + N - 1] (clamped to the table).
        for (const p of pending) {
            let total = 0;
            const end = Math.min(p.rowIdx + p.rowSpan, bands.length);
            for (let j = p.rowIdx; j < end; j++) total += bands[j];
            if (total > 0) p.block.rowSpan = total;
        }

        const totalBand = cursor - tableBase + 1;
        return {
            node: table,
            rowStart: tableBase,
            rowSpan: totalBand,
            colStart: 1,
            colSpan: 1,
            subgrid: {
                rows: totalBand,
                cols: layout.colCount,
                children: cells,
                colFractions: deriveColumnFractions(layout.placements, layout.colCount),
                ...(opts?.tableToolbar && tableId != null && {
                    toolbar: { tableId, columnLabels: columnLabels(table) },
                }),
            },
        };
    };

    const placeTop = (node: HastNode, tableId?: string): PlacedBlock => {
        if (isElement(node)) {
            if (node.tagName === "table") return placeTable(node, tableId);
            if (node.tagName === "ul" || node.tagName === "ol") return placeList(node);
        }
        return placeLeaf(node);
    };

    // Table ids, resolved BEFORE the renderability filter and counted over every
    // table in the subtree — exactly how applyTableVisibility numbers them. Doing
    // it inside placeTable instead would skip tables that the filter drops (a
    // table whose columns are all hidden renders empty and is dropped), and the
    // next table of the same element would then be handed its predecessor's id —
    // the toolbar would hide columns of the wrong table.
    // Skipped unless a toolbar is actually being drawn: segment() re-runs on
    // every annotation change, and nothing else consumes these ids.
    const tableCounts = new Map<string | undefined, number>();
    const tableIds = opts?.tableToolbar
        ? nodes.map((node, i) => {
              const elementId = elementIds?.[i];
              const before = tableCounts.get(elementId) ?? 0;
              tableCounts.set(elementId, before + countTables(node));
              // A top-level <table> is, in document order, the first table of its
              // own subtree — so it takes the counter's value going in.
              return isElement(node) && node.tagName === "table"
                  ? makeTableId(elementId, before)
                  : undefined;
          })
        : [];

    // Block ids, like the table ids, are resolved BEFORE the renderability filter
    // so an element's second node keeps its ordinal even when its first is
    // dropped as empty. Always computed: hiding a block is done by id in BOTH
    // modes, unlike the toolbar which only exists in advanced mode.
    const nodeOrdinals = new Map<string | undefined, number>();
    const blockIds = nodes.map((_, i) => {
        const elementId = elementIds?.[i];
        const ordinal = nodeOrdinals.get(elementId) ?? 0;
        nodeOrdinals.set(elementId, ordinal + 1);
        return makeBlockId(elementId, ordinal);
    });

    // Top-level empties (whitespace, blank paragraphs, comments) are dropped so
    // they don't inject empty grid rows. `plan.content[i].node` is the surviving
    // node — callers that index alongside content must read it from here. Carry
    // each surviving node's source element id in lockstep with the same filter.
    const content = nodes
        .map((node, i) => ({
            node,
            elementId: elementIds?.[i],
            tableId: tableIds[i],
            blockId: blockIds[i],
        }))
        .filter((t) => isRenderableTop(t.node))
        .map((t) => {
            const block = placeTop(t.node, t.tableId);
            if (t.elementId != null) block.elementId = t.elementId;
            block.blockId = t.blockId;
            return block;
        });

    const rows: GridRow[] = [];
    for (let r = 1; r <= cursor; r++) {
        rows.push({
            row: r,
            gutter: gutter.get(r) ?? { kind: "none" },
            dept: dept.get(r),
            docLink: docLink.get(r),
        });
    }

    return { rowCount: cursor, rows, content };
}
